import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { LocalesService } from "src/app/shared/services/locales/locales.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { AppState } from "../../reducers";
import { from } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, tap } from "rxjs/operators";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";

import * as LocalesActionTypes from "../../actions/locales/locales.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

@Injectable()
export class LocalesEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private localeService: LocalesService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService
  ) {}

  getLocalesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.getLocalesList),
      exhaustMap((action) =>
        this.localeService
          .getLocalesList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              LocalesActionTypes.loadLocalesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                LocalesActionTypes.loadLocalesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Locales.loadLocalesListError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getExcelLocalesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.getExcelLocalesList),
      exhaustMap((action) =>
        this.localeService
          .getLocalesList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              LocalesActionTypes.loadExcelLocalesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                LocalesActionTypes.loadExcelLocalesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Locales.loadExcelLocalesListError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.getLocale),
      exhaustMap((action) =>
        this.localeService.getLocale(action.code).pipe(
          map((result) => LocalesActionTypes.loadLocale({ response: result })),
          catchError((error) => {
            const actions = [
              LocalesActionTypes.localeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Locales.loadLocaleError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.createLocale),
      exhaustMap((action) =>
        this.localeService.createLocale(action.locale).pipe(
          mergeMap((result) => [
            LocalesActionTypes.resetCreation(),
            LocalesActionTypes.getLocale({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Locales.localeCreated"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              LocalesActionTypes.localeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Locales.localeCreateError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  updateGrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.updateLocale),
      exhaustMap((action) =>
        this.localeService.updateLocale(action.locale).pipe(
          map((result) => {
            return LocalesActionTypes.getLocale({
              code: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Locales.localeUpdated"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              LocalesActionTypes.localeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Locales.localeUpdateError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Products Table Config
  getLocalesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.getLocalesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              LocalesActionTypes.loadLocalesTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                LocalesActionTypes.loadLocalesTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("TableConfig.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Products Table Config
  saveGradesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalesActionTypes.saveLocalesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            LocalesActionTypes.localeLoadEnd(),
            MessagesActionTypes.setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              LocalesActionTypes.localeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("TableConfig.SaveError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
}
